const PROCESSOR_PREFIX = /\b(?:SQ|TST|PY|VENMO|TC)\s*\*\s*/g;
const HASH_NUMBER     = /#\s*\d+/g;
const LONG_DIGITS     = /\b\d{3,}\b/g;
const TRAILING_STATE  = /\s+[A-Z]{2}$/;

/**
 * Shared tail: uppercase, drop processor prefixes, store numbers, and reference
 * codes, then squash to letters/digits/spaces.
 *
 * Idempotent by construction — every step here only removes characters/patterns
 * or collapses whitespace, so running it on its own output changes nothing. The
 * state code is deliberately NOT stripped here: `\s+[A-Z]{2}$` matches any
 * trailing two-letter token, not just a US state, so digit-stripping run inside
 * this function can expose a truncated word (e.g. "...SNACK BA" after "760-3425"
 * is dropped) that a second pass would then wrongly eat. Callers that know the
 * trailing token really is a state (normalizeBank) must strip it themselves,
 * on the raw string, before calling in here.
 */
export function normalizeStem(value: string): string {
  let s = (value ?? '').toUpperCase();
  s = s.replace(PROCESSOR_PREFIX, ' ');
  s = s.replace(HASH_NUMBER, ' ');
  s = s.replace(LONG_DIGITS, ' ');
  s = s.replace(/[^A-Z0-9&' ]+/g, ' ');
  return s.replace(/\s+/g, ' ').trim();
}

/** Apple's Merchant column is clean; its Description mashes in the address. */
export function normalizeApple(merchant: string, description: string): string {
  const source = (merchant ?? '').trim() || (description ?? '');
  return normalizeStem(source);
}

const BANK_VERB   = /^(?:withdrawal|deposit|w\/d)\s+/i;
const BANK_METHOD = /^(?:debit card visa debit|visa debit|pos\s*#?\s*\d*|ach|online)\s+/i;

/**
 * The bank's description format drifted across five years, so this is layered
 * with a pass-through fallback rather than one regex.
 *
 * Truncating at the newline is safe — verified against the real export, zero rows
 * carry their only transfer signal on a continuation line.
 *
 * `home banking transfer` / `online banking transfer` are deliberately NOT in
 * BANK_METHOD: they are the transaction-type phrase, not a noise prefix, and
 * seeded transfer rules match on it (e.g. 'Withdrawal Home Banking Transfer To
 * Loan 09' must keep 'HOME BANKING TRANSFER' as signal). The bare `online`
 * alternative, in contrast, IS kept as a noise prefix: measured against the
 * real 1,510-row export, stripping it never eats any of the six seeded
 * transfer patterns (0 rows lose a match because of it — the union of
 * seed-pattern hits is 806 whether `online` is stripped or left in), so it
 * does not have the same defect as the two phrases removed above.
 *
 * The trailing two-letter STATE is stripped here, on the raw line, before the
 * string ever reaches normalizeStem — not the city. Greedy city-stripping
 * mangles multi-word merchants ('MINT SUSHI SANDY' -> 'MINT'), and leaving the
 * city in is harmless because it is stable per merchant. Stripping it here
 * (rather than inside normalizeStem) is what keeps normalizeStem idempotent:
 * by the time digit-stripping could expose a truncated trailing word, the real
 * state token is already gone and there is nothing left to wrongly re-strip.
 */
export function normalizeBank(description: string): string {
  const firstLine = (description ?? '').split('\n')[0].trim();
  let s = firstLine;

  s = s.replace(BANK_VERB, '');
  s = s.replace(BANK_METHOD, '');

  // Layer 5: if stripping consumed everything, keep the original line.
  if (!s.trim()) s = firstLine;

  s = s.replace(TRAILING_STATE, '');

  return normalizeStem(s);
}
